import React, { useState, useRef, forwardRef } from 'react';
import PropTypes from 'prop-types';

import styles from './DigitInput.css';

const MAX_DIGITS = 6;

const SingleInput = forwardRef((props, ref) => (
  <input
    autoCapitalize={'off'}
    autoComplete={'off'}
    autoCorrect={'off'}
    inputMode={'tel'}
    maxLength={'1'}
    pattern={'[0-9]'}
    ref={ref}
    spellCheck={false}
    onChange={e => {
      e.preventDefault();
    }}
    {...props}
  />
));

const DigitInput = ({
  onComplete = () => {},
  onChange = () => {},
  autoFocus = false,
  message,
}) => {
  const rangeArray = [...Array(MAX_DIGITS).keys()];
  const inputRefs = rangeArray.map(() => useRef());
  const [digits, setDigits] = useState(rangeArray.map(() => ''));

  const setState = digits => {
    setDigits(digits);

    onChange(digits.join(''));

    if (digits.join('').length === MAX_DIGITS) {
      onComplete();
    }
  };

  const changeDigit = (index, value) => {
    const newDigits = digits;
    newDigits[index] = value;
    setState(newDigits);
  };

  const jumpFocus = nextIndex => {
    if (nextIndex >= MAX_DIGITS || nextIndex < 0) {
      return;
    }

    inputRefs[nextIndex].current.focus();
    inputRefs[nextIndex].current.select();
  };

  const onKeyDown = e => {
    const { key, target, altKey, ctrlKey, metaKey, shiftKey } = e;
    const isModifierKey =
      altKey || ctrlKey || metaKey || shiftKey || key === 'Tab';

    if (isModifierKey) return;

    const idx = parseInt(target.attributes.index.value);
    const isNumber = Number.isInteger(parseInt(key));
    const inputIsEmpty = digits[idx] === '';

    e.preventDefault();

    const keyActions = {
      ArrowLeft: () => {
        jumpFocus(idx - 1);
      },
      ArrowRight: () => {
        jumpFocus(idx + 1);
      },
      Backspace: () => {
        if (inputIsEmpty) {
          changeDigit(idx - 1, '');
          jumpFocus(idx - 1);
        } else {
          changeDigit(idx, '');
        }
      },
      default: () => {
        if (isNumber) {
          changeDigit(idx, key);
          jumpFocus(idx + 1);
        }
      },
    };

    (keyActions[key] || keyActions.default)();
  };

  const onPaste = e => {
    if (!e.clipboardData) return;

    const pasteString = e.clipboardData.getData('Text').slice(0, MAX_DIGITS);

    setState(pasteString.split(''));
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.digitInputContainer}>
        {rangeArray.map(index => (
          <SingleInput
            key={index}
            className={styles.digitInput}
            autoFocus={autoFocus && index === 0}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            value={digits[index]}
            index={index}
            ref={inputRefs[index]}
          />
        ))}
      </div>
      <div className={styles.errorMessage}>{message}</div>
    </div>
  );
};

DigitInput.propTypes = {
  onChange: PropTypes.func,
  onComplete: PropTypes.func,
  autoFocus: PropTypes.bool,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

export default DigitInput;
